import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { dateFmt } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, AlertTriangle, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  Present: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Absent: "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400",
  "Half Day": "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
  "On Leave": "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  Holiday: "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400",
  Weekend: "bg-muted text-muted-foreground",
};
const ATTENDANCE_STATUSES = ["Present", "Absent", "Half Day", "On Leave", "Holiday", "Weekend"];

function monthLabel(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function shiftMonth(month, delta) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function MarkAttendanceSection() {
  const qc = useQueryClient();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const { data: marks, isLoading } = useQuery({ queryKey: ["attendance-marks", date], queryFn: () => get(`/hrms/attendance/marks?date=${date}`) });
  const [edits, setEdits] = useState({});

  const save = useMutation({
    mutationFn: () => post("/hrms/attendance/marks", {
      date, marks: Object.entries(edits).map(([employee_id, status]) => ({ employee_id, status })),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["attendance-marks", date] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      toast.success("Attendance saved");
      setEdits({});
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save attendance"),
  });

  const statusFor = (row) => edits[row.employee_id] ?? row.status;

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Input type="date" className="w-44" data-testid="mark-date" value={date} onChange={(e) => { setDate(e.target.value); setEdits({}); }} />
        <Button size="sm" data-testid="mark-save-all" onClick={() => save.mutate()} disabled={!Object.keys(edits).length || save.isPending}>
          <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Save Attendance{Object.keys(edits).length ? ` (${Object.keys(edits).length})` : ""}
        </Button>
      </div>
      {isLoading ? <Skeleton className="h-96" /> : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Employee</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Source</th></tr></thead>
            <tbody>
              {marks?.map((row) => (
                <tr key={row.employee_id} data-testid={`mark-row-${row.employee_id}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar user={{ name: row.name }} size="xs" /><span className="font-medium">{row.name}</span></div></td>
                  <td className="px-4 py-3">
                    <Select value={statusFor(row)} onValueChange={(v) => setEdits((e) => ({ ...e, [row.employee_id]: v }))}>
                      <SelectTrigger className="h-8 w-36" data-testid={`mark-status-${row.employee_id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{ATTENDANCE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {edits[row.employee_id] !== undefined ? "Unsaved change" : row.is_manual ? "Marked by HR" : "Suggested (auto)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Attendance() {
  const { can } = useApp();
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}`;
  });
  const { data, isLoading } = useQuery({ queryKey: ["attendance", month], queryFn: () => get(`/hrms/attendance?month=${month}`) });

  const mismatches = useMemo(() => {
    const rows = [];
    (data || []).forEach((row) => {
      row.days.forEach((d) => {
        if (d.hours_worked != null && d.timesheet_hours != null && Math.abs(d.hours_worked - d.timesheet_hours) > 1) {
          rows.push({ employee_id: row.employee_id, name: row.name, ...d, diff: +(d.hours_worked - d.timesheet_hours).toFixed(1) });
        }
      });
    });
    return rows.sort((a, b) => b.date.localeCompare(a.date));
  }, [data]);

  return (
    <div>
      <PageHeader title="Attendance" subtitle="Present, on-leave, and holiday status by working day.">
        <div className="flex items-center gap-2">
          <Button size="icon" variant="outline" data-testid="attendance-prev" onClick={() => setMonth((m) => shiftMonth(m, -1))}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="w-36 text-center text-sm font-medium">{monthLabel(month)}</span>
          <Button size="icon" variant="outline" data-testid="attendance-next" onClick={() => setMonth((m) => shiftMonth(m, 1))}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </PageHeader>

      <Tabs defaultValue="calendar">
        <TabsList data-testid="attendance-tabs" className="mb-4">
          <TabsTrigger value="calendar" data-testid="attendance-tab-calendar">Calendar</TabsTrigger>
          <TabsTrigger value="mismatches" data-testid="attendance-tab-mismatches">
            Mismatch Report{mismatches.length > 0 ? ` (${mismatches.length})` : ""}
          </TabsTrigger>
          {can("hrms", "edit") && <TabsTrigger value="mark" data-testid="attendance-tab-mark">Mark Attendance</TabsTrigger>}
        </TabsList>

        <TabsContent value="calendar">
          <div className="mb-4 flex flex-wrap gap-3 text-xs">
            {Object.entries(STATUS_STYLE).map(([label, cls]) => (
              <span key={label} className="flex items-center gap-1.5"><span className={cn("h-3 w-3 rounded-sm", cls)} />{label}</span>
            ))}
            <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500" />Attendance / Timesheet hours mismatch</span>
          </div>

          {isLoading ? <Skeleton className="h-96" /> : (
            <div className="overflow-x-auto rounded-xl border border-border bg-card">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="sticky left-0 z-10 bg-card px-3 py-2 font-semibold">Employee</th>
                    {data?.[0]?.days.map((d) => (
                      <th key={d.date} className="px-1 py-2 text-center font-medium">{d.date.slice(-2)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data?.map((row) => (
                    <tr key={row.employee_id} data-testid={`attendance-row-${row.employee_id}`} className="border-b border-border last:border-0">
                      <td className="sticky left-0 z-10 bg-card px-3 py-2">
                        <div className="flex items-center gap-2"><Avatar user={{ name: row.name }} size="xs" /><span className="font-medium">{row.name}</span></div>
                      </td>
                      {row.days.map((d) => {
                        const mismatch = d.hours_worked != null && d.timesheet_hours != null && Math.abs(d.hours_worked - d.timesheet_hours) > 1;
                        const title = `${d.date}: ${d.status}` +
                          (d.hours_worked != null ? ` · Attendance: ${d.hours_worked}h` : "") +
                          (d.timesheet_hours != null ? ` · Timesheet: ${d.timesheet_hours}h` : "");
                        return (
                          <td key={d.date} className="relative px-1 py-2 text-center" title={title}>
                            <div className={cn("mx-auto h-4 w-4 rounded-sm", STATUS_STYLE[d.status])} />
                            {mismatch && <div className="absolute right-0 top-0.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-card" />}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="mismatches">
          {isLoading ? <Skeleton className="h-64" /> : !mismatches.length ? (
            <EmptyState icon={AlertTriangle} title="No mismatches this month" description="Every day with logged attendance hours matches its timesheet total." />
          ) : (
            <div className="overflow-hidden rounded-xl border border-border bg-card">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Employee</th>
                  <th className="px-4 py-3 font-semibold">Date</th>
                  <th className="px-3 py-3 text-right font-semibold">Attendance Hours</th>
                  <th className="px-3 py-3 text-right font-semibold">Timesheet Hours</th>
                  <th className="px-3 py-3 text-right font-semibold">Difference</th></tr></thead>
                <tbody>
                  {mismatches.map((m, i) => (
                    <tr key={i} data-testid={`mismatch-row-${m.employee_id}-${m.date}`} className="border-b border-border last:border-0">
                      <td className="px-4 py-3"><div className="flex items-center gap-2"><Avatar user={{ name: m.name }} size="xs" /><span className="font-medium">{m.name}</span></div></td>
                      <td className="px-4 py-3 text-muted-foreground">{dateFmt(m.date)}</td>
                      <td className="px-3 py-3 text-right font-mono">{m.hours_worked}h</td>
                      <td className="px-3 py-3 text-right font-mono">{m.timesheet_hours}h</td>
                      <td className={cn("px-3 py-3 text-right font-mono font-semibold", m.diff > 0 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400")}>
                        {m.diff > 0 ? "+" : ""}{m.diff}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        {can("hrms", "edit") && (
          <TabsContent value="mark">
            <MarkAttendanceSection />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
